import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { Underline } from "@tiptap/extension-underline";
import { Link } from "@tiptap/extension-link";
import { Image } from "@tiptap/extension-image";
import { TextAlign } from "@tiptap/extension-text-align";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Typography } from "@tiptap/extension-typography";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { CodeBlockLowlight } from "@tiptap/extension-code-block-lowlight";
import { common, createLowlight } from "lowlight";
import { marked } from "marked";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Code, Link as LinkIcon,
  List, ListOrdered, Quote, Minus, Undo, Redo,
  Heading1, Heading2, Heading3, Heading4, Heading5, Heading6,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  ImagePlus, Table as TableIcon, RemoveFormatting,
} from "lucide-react";

const lowlight = createLowlight(common);

const BTN_BASE = "p-1.5 rounded-md text-foreground/60 hover:text-foreground hover:bg-foreground/5 transition-colors";
const BTN_ACTIVE = "bg-[#D4AF37]/15 text-[#D4AF37]";

function ToolbarButton({ onClick, active, title, disabled, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      data-testid={testId}
      className={`${BTN_BASE} ${active ? BTN_ACTIVE : ""} disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

function Divider() { return <span className="w-px h-6 bg-foreground/10 mx-0.5" />; }

function Toolbar({ editor }) {
  if (!editor) return null;

  const addLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Link URL", prev || "https://");
    if (url === null) return;
    if (url === "") return editor.chain().focus().unsetLink().run();
    editor.chain().focus().extendMarkRange("link").setLink({ href: url, target: "_blank", rel: "noopener noreferrer" }).run();
  };

  const uploadImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file.");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be under 5 MB.");
    const fd = new FormData();
    fd.append("file", file);
    const toastId = toast.loading("Uploading image…");
    try {
      const { data } = await api.post("/admin/blog/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const src = data.url.startsWith("http") ? data.url : `${backend}${data.url}`;
      editor.chain().focus().setImage({ src, alt: file.name }).run();
      toast.success("Image inserted", { id: toastId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed", { id: toastId });
    }
  };

  const pickImage = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = () => uploadImage(input.files?.[0]);
    input.click();
  };

  const insertTable = () => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();

  const HEADINGS = [
    { level: 1, Icon: Heading1 }, { level: 2, Icon: Heading2 }, { level: 3, Icon: Heading3 },
    { level: 4, Icon: Heading4 }, { level: 5, Icon: Heading5 }, { level: 6, Icon: Heading6 },
  ];

  return (
    <div className="sticky top-0 z-10 flex flex-wrap items-center gap-0.5 p-2 border border-foreground/10 rounded-t-2xl bg-background" data-testid="editor-toolbar">
      <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo" testId="tb-undo"><Undo className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo" testId="tb-redo"><Redo className="w-4 h-4" /></ToolbarButton>
      <Divider />
      {HEADINGS.map(({ level, Icon }) => (
        <ToolbarButton
          key={level}
          onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
          active={editor.isActive("heading", { level })}
          title={`Heading ${level}`}
          testId={`tb-h${level}`}
        ><Icon className="w-4 h-4" /></ToolbarButton>
      ))}
      <Divider />
      <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold (Ctrl+B)" testId="tb-bold"><Bold className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic (Ctrl+I)" testId="tb-italic"><Italic className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline (Ctrl+U)" testId="tb-underline"><UnderlineIcon className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Strikethrough" testId="tb-strike"><Strikethrough className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive("code")} title="Inline code" testId="tb-code"><Code className="w-4 h-4" /></ToolbarButton>
      <Divider />
      <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list" testId="tb-bullet"><List className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list" testId="tb-ordered"><ListOrdered className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Blockquote" testId="tb-quote"><Quote className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive("codeBlock")} title="Code block" testId="tb-codeblock"><Code className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal rule" testId="tb-hr"><Minus className="w-4 h-4" /></ToolbarButton>
      <Divider />
      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Align left" testId="tb-align-left"><AlignLeft className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Align center" testId="tb-align-center"><AlignCenter className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Align right" testId="tb-align-right"><AlignRight className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("justify").run()} active={editor.isActive({ textAlign: "justify" })} title="Justify" testId="tb-align-justify"><AlignJustify className="w-4 h-4" /></ToolbarButton>
      <Divider />
      <ToolbarButton onClick={addLink} active={editor.isActive("link")} title="Insert / edit link" testId="tb-link"><LinkIcon className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={pickImage} title="Insert image" testId="tb-image"><ImagePlus className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={insertTable} title="Insert table" testId="tb-table"><TableIcon className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} title="Clear formatting" testId="tb-clear"><RemoveFormatting className="w-4 h-4" /></ToolbarButton>
    </div>
  );
}

/** Detect Markdown-heavy pastes (from ChatGPT etc.) and convert to HTML on the fly. */
function looksLikeMarkdown(text) {
  if (!text) return false;
  const t = text.trim();
  // Common markdown patterns: headings, lists, bold**, links [text](url), fenced code
  return /^#{1,6}\s|\n#{1,6}\s|^\*\s|\n\*\s|^-\s|\n-\s|\n\d+\.\s|\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\)|```/.test(t);
}

/** Editor with rich toolbar. Value/onChange in HTML. */
export function RichTextEditor({ value, onChange, placeholder = "Start writing your article…" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5, 6] },
        codeBlock: false, // replaced by lowlight variant below
        link: false,      // configured explicitly below with custom attrs
        underline: false, // configured explicitly below
      }),
      Underline,
      Typography,
      Placeholder.configure({ placeholder }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: "text-[#D4AF37] underline underline-offset-2 hover:opacity-80" } }),
      Image.configure({ HTMLAttributes: { class: "rounded-xl my-4 max-w-full h-auto" } }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Table.configure({ resizable: true, HTMLAttributes: { class: "border-collapse my-4 w-full" } }),
      TableRow,
      TableHeader.configure({ HTMLAttributes: { class: "border border-foreground/20 bg-foreground/5 p-2 font-semibold text-left" } }),
      TableCell.configure({ HTMLAttributes: { class: "border border-foreground/20 p-2 align-top" } }),
      CodeBlockLowlight.configure({
        lowlight,
        HTMLAttributes: { class: "rounded-xl bg-[#0B1F3A] text-[#F5E8D8] p-4 my-3 overflow-x-auto text-sm font-mono" },
      }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "astro-prose max-w-none focus:outline-none min-h-[420px] p-5",
        "data-testid": "rte-content",
      },
      transformPastedHTML(html) {
        // Word and Google Docs pastes come with excessive inline styles / XML.
        // Strip mso-* junk and <o:p> tags but keep semantic elements.
        return html
          .replace(/<!--[\s\S]*?-->/g, "")
          .replace(/<o:p[^>]*>[\s\S]*?<\/o:p>/gi, "")
          .replace(/<w:[^>]+>[\s\S]*?<\/w:[^>]+>/gi, "")
          .replace(/\sclass="Mso[^"]*"/gi, "")
          .replace(/\sstyle="mso-[^"]*"/gi, "")
          .replace(/\sxmlns:[a-z]+="[^"]*"/gi, "")
          .replace(/\s(?:lang|dir)="[^"]*"/gi, "");
      },
      handlePaste(view, event) {
        const text = event.clipboardData?.getData("text/plain") || "";
        const html = event.clipboardData?.getData("text/html") || "";
        // If it's plain-text markdown (ChatGPT, README files, notes) — convert.
        if (!html && looksLikeMarkdown(text)) {
          event.preventDefault();
          const rendered = marked.parse(text, { breaks: true, gfm: true });
          editor?.chain().focus().insertContent(rendered).run();
          return true;
        }
        return false;
      },
    },
    onUpdate({ editor }) {
      onChange?.(editor.getHTML());
    },
  });

  // Keep external value changes in sync (rare, e.g. loading an existing post).
  useEffect(() => {
    if (!editor) return;
    if (value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  return (
    <div className="border-x border-b border-foreground/10 rounded-b-2xl bg-background overflow-hidden">
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
}

/** Read-only preview using the same TipTap styles. Renders raw HTML. */
export function RichTextPreview({ html }) {
  return (
    <article
      data-testid="rte-preview"
      className="astro-prose max-w-none p-6 rounded-2xl border border-foreground/10 bg-background min-h-[480px]"
      dangerouslySetInnerHTML={{ __html: html || '<p class="text-foreground/40 italic">Nothing to preview yet — start writing.</p>' }}
    />
  );
}

export default RichTextEditor;
